// Modules
import { useState, createContext, useContext, useEffect } from "react"

// Constants
import SESSION_KEYS from "~constants/session"
import { UserIdentity } from "~constants/application"
import { ERROR_MESSAGES } from "~constants/errorMessages"

// Services
import AuthService, { AuthenticatePayload } from "~services/AuthService"

// Utils
import { extractUserIdentity } from "~utils/user"
import { storeItem, getStoredItem, removeStoredItem } from "~utils/store"
import { setMemoryToken, getMemoryToken } from "~utils/auth-token"

// Interfaces
import { CompanyShape } from "~shapes/company"
import { getFullImagePath } from "~utils/utils"

interface AuthContextProps {
  loggedUser: UserIdentity
  loggedCompany: CompanyShape
  isLoading: boolean
  isLoggedIn: () => boolean
  handleLogin: (_payload: AuthenticatePayload) => Promise<any>
  handleLogout: () => Promise<any>
  handlePasswordReset: (_code: string) => Promise<any>
  isResettingPassword: () => boolean
  getLoggedUserCompany: (_user?: UserIdentity) => void
}

const DefaultAuthContext: AuthContextProps = {
  loggedUser: null!,
  loggedCompany: null!,
  isLoading: true,
  isLoggedIn: () => false,
  handleLogin: (_payload: AuthenticatePayload) => Promise.reject(),
  handleLogout: () => Promise.resolve(),
  handlePasswordReset: (_code: string) => Promise.reject(),
  isResettingPassword: () => false,
  getLoggedUserCompany: (_user?: UserIdentity) => {},
}

export const AuthContext = createContext<AuthContextProps>(DefaultAuthContext)

export const useAuthContext = () => {
  return useContext(AuthContext)
}

const AuthProvider = ({ children }) => {
  // Provider state
  const [user, setUser] = useState<UserIdentity>(null!)
  const [company, setCompany] = useState<CompanyShape>(null!)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // On mount: attempt silent session restore via HttpOnly cookie
  useEffect(() => {
    AuthService.spaSession()
      .then(async data => {
        if (!data?.access_token) return

        setMemoryToken(data.access_token)
        const loggedUser = extractUserIdentity(data.access_token)

        const storedCompany = getStoredItem(SESSION_KEYS.LOGGED_USER_COMPANY)
        if (storedCompany) {
          setCompany(JSON.parse(storedCompany))
        }

        setUser(loggedUser)
        // Refresh company info in the background
        getLoggedUserCompany(loggedUser)
      })
      .catch(() => {
        setMemoryToken(null)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Method to handle the login
  const handleLogin = async (payload: AuthenticatePayload) => {
    try {
      const data = await AuthService.spaLogin(payload)

      setMemoryToken(data.access_token)

      const loggedUser = extractUserIdentity(data.access_token)

      await getLoggedUserCompany(loggedUser)

      setUser(loggedUser)
    } catch (err) {
      return Promise.reject(err)
    }
  }

  // Method to handle password reset (uses legacy OAuth code exchange)
  const handlePasswordReset = async (code: string) => {
    try {
      const resp = await AuthService.getAccessTokenResetPassword(code)
      const { access_token } = resp

      setMemoryToken(access_token)
      storeItem(SESSION_KEYS.IS_RESETTING_PASSWORD, "true")

      const loggedUser = extractUserIdentity(access_token)
      setUser(loggedUser)
    } catch (err) {
      throw new Error(ERROR_MESSAGES.GENERIC)
    }
  }

  // Method to get logged user company
  const getLoggedUserCompany = async (loggedUser?: UserIdentity) => {
    try {
      if (!loggedUser?.id) return

      const { data } = await AuthService.getCompanyFromInfoUrl(loggedUser.id)
      const company = data
      company.logo = getFullImagePath(company.logo)

      storeItem(SESSION_KEYS.LOGGED_USER_COMPANY, JSON.stringify(company))
      setCompany(company)
    } catch (err) {}
  }

  const handleLogout = (): Promise<any> => {
    return new Promise<void>(resolve => {
      setUser(null!)
      setCompany(null!)
      setMemoryToken(null)

      AuthService.spaLogout().finally(() => {
        removeStoredItem(SESSION_KEYS.LOGGED_USER_COMPANY)
        removeStoredItem(SESSION_KEYS.IS_RESETTING_PASSWORD)
        resolve()
      })
    })
  }

  // Method to check if the user is resetting the password
  const isResettingPassword = (): boolean => {
    const resettingPassword = getStoredItem(SESSION_KEYS.IS_RESETTING_PASSWORD)
    return !!resettingPassword
  }

  // Method to check if the user is logged in (memory-only token)
  const isLoggedIn = (): boolean => {
    return !!getMemoryToken() && !isResettingPassword()
  }

  return (
    <AuthContext.Provider
      value={{
        loggedUser: user,
        loggedCompany: company,
        isLoading,
        isLoggedIn,
        handleLogin,
        handleLogout,
        handlePasswordReset,
        isResettingPassword,
        getLoggedUserCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthProvider }
